import { create } from "zustand";
import { persist } from "zustand/middleware";

const MAX_NOTIFICATIONS = 20;
const MAX_RECENT_PAGES = 5;
const MAX_PINNED_PAGES = 5;

export interface AppNotification {
  id: string;
  type: "info" | "success" | "warning" | "error";
  title: string;
  message?: string;
  createdAt: number;
}

interface AppState {
  // Sidebar
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Notifications
  notifications: AppNotification[];
  addNotification: (n: Omit<AppNotification, "id" | "createdAt">) => void;
  dismissNotification: (id: string) => void;
  clearNotifications: () => void;

  // Command palette
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;

  // Recent pages
  recentPages: string[];
  addRecentPage: (path: string) => void;

  // Pinned pages (favorites)
  pinnedPages: string[];
  togglePin: (path: string) => void;

  // Theme
  theme: "light" | "dark" | "system";
  setTheme: (theme: "light" | "dark" | "system") => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      notifications: [],
      addNotification: (n) =>
        set((s) => {
          const updated = [
            ...s.notifications,
            { ...n, id: crypto.randomUUID(), createdAt: Date.now() },
          ];
          return {
            notifications:
              updated.length > MAX_NOTIFICATIONS
                ? updated.slice(-MAX_NOTIFICATIONS)
                : updated,
          };
        }),
      dismissNotification: (id) =>
        set((s) => ({
          notifications: s.notifications.filter((n) => n.id !== id),
        })),
      clearNotifications: () => set({ notifications: [] }),

      commandPaletteOpen: false,
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),

      recentPages: [],
      addRecentPage: (path) =>
        set((s) => {
          if (s.recentPages[0] === path) return s;
          const filtered = s.recentPages.filter((p) => p !== path);
          return {
            recentPages: [path, ...filtered].slice(0, MAX_RECENT_PAGES),
          };
        }),

      pinnedPages: [],
      togglePin: (path) =>
        set((s) => {
          if (s.pinnedPages.includes(path)) {
            return { pinnedPages: s.pinnedPages.filter((p) => p !== path) };
          }
          if (s.pinnedPages.length >= MAX_PINNED_PAGES) return s;
          return { pinnedPages: [...s.pinnedPages, path] };
        }),

      theme: "system",
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: "agent-hub-settings",
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
        recentPages: state.recentPages,
        pinnedPages: state.pinnedPages,
      }),
    },
  ),
);
